import { useState } from "react";
import { Select, Space, Typography, message } from "antd";
import { UserOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { ApiService } from "../api";
import { DOMAIN, getAccessToken } from "../constants";

const { Text } = Typography;

type Props = {
  requestId: number;
  formId: number;
  groupId?: number | null;
  groupName?: string | null;
  assigneeId?: string | null;
  assigneeName?: string | null;
  onChanged: () => void;
};

/**
 * Lets the requester pick one specific person (from the section's group) to fill
 * a group-owned section on this request. Empty = anyone in the group (default).
 * Uses the request-section/assign endpoint directly (not yet in the generated
 * client).
 */
const SectionAssigneePicker = ({
  requestId,
  formId,
  groupId,
  groupName,
  assigneeId,
  assigneeName,
  onChanged,
}: Props) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState<Array<{ value: string; label: string }>>(
    assigneeId && assigneeName ? [{ value: assigneeId, label: assigneeName }] : []
  );
  const [loading, setLoading] = useState(false);

  const search = async (value: string) => {
    if (!groupId) return;
    try {
      const users = await ApiService.apiAccountsUsersList(value, groupId as any);
      setOptions(
        (users as any[]).map((u) => ({ value: u.id, label: u.name }))
      );
    } catch {
      /* ignore search errors */
    }
  };

  const assign = async (userId: string | null) => {
    setLoading(true);
    try {
      const res = await window.fetch(
        `${DOMAIN}/api/main/request-section/assign/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAccessToken()}`,
          },
          body: JSON.stringify({
            request_id: requestId,
            form_id: formId,
            user_id: userId,
          }),
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail || body?.[0] || "");
      }
      message.success(userId ? t("Section assigned") : t("Assignment cleared"));
      onChanged();
    } catch (e: any) {
      message.error(e?.message || t("Something went wrong"));
    }
    setLoading(false);
  };

  return (
    <Space size="small" wrap>
      <Text type="secondary">
        <UserOutlined />{" "}
        {t("Assign to a {{group}} member", { group: groupName ?? "" })}:
      </Text>
      <Select
        showSearch
        allowClear
        loading={loading}
        style={{ minWidth: 220 }}
        placeholder={t("Anyone in the group")}
        value={assigneeId ?? undefined}
        filterOption={false}
        onSearch={search}
        onChange={(v) => assign((v as string) ?? null)}
        onClear={() => assign(null)}
        options={options}
      />
    </Space>
  );
};

export default SectionAssigneePicker;
